import React, { useState, useEffect, useRef } from 'react';
import '../assets/AllTabsCommon.css';
import '../assets/Reports.css';
import '../assets/DeviceTimeline.css';
import { fetchDeviceTimeline } from '../api/restapi';

function DeviceTimeline({ prefilledSystemId = '' }) {
    // State management
    const [systemId, setSystemId] = useState(prefilledSystemId);
    const [duration, setDuration] = useState('24h');
    const [sortDirection, setSortDirection] = useState('asc'); // New sort direction state
    const [eventsOnly, setEventsOnly] = useState(false);
    const [events, setEvents] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [rdSessionSummary, setRdSessionSummary] = useState(null);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(50); // New items per page state
    const [selectedPage, setSelectedPage] = useState(1); // New selected page state
    
    // Category filters
    const [categoryFilters, setCategoryFilters] = useState({
        CONNECTION: true,
        REGISTRATION: true,
        SESSION: true,
        UPLOAD: true,
        MQTT: true,
        URL_FETCH: true
    });
    
    // Internal pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [allEvents, setAllEvents] = useState([]);
    const [hasMoreData, setHasMoreData] = useState(true);
    
    // Constants
    const pageSize = 50;
    const sortBy = 'ts';
    const sortDir = 'asc';
    
    // Time range options
    const timeRangeOptions = [
        { value: '5m', label: 'Last 5 minutes' },
        { value: '15m', label: 'Last 15 minutes' },
        { value: '1h', label: 'Last 1 hour' },
        { value: '6h', label: 'Last 6 hours' },
        { value: '12h', label: 'Last 12 hours' },
        { value: '24h', label: 'Last 24 hours' },
        { value: '2d', label: 'Last 2 days' },
        { value: '3d', label: 'Last 3 days' },
        { value: '4d', label: 'Last 4 days' },
        { value: '5d', label: 'Last 5 days' },
        { value: '6d', label: 'Last 6 days' },
        { value: '7d', label: 'Last 7 days' },
        { value: '30d', label: 'Last 30 days' }
    ];

    // Sort direction options
    const sortOptions = [
        { value: 'desc', label: 'Newest First (DESC)' },
        { value: 'asc', label: 'Oldest First (ASC)' }
    ];

    // Items per page options
    const itemsPerPageOptions = [
        { value: 25, label: '25 items' },
        { value: 50, label: '50 items' },
        { value: 100, label: '100 items' }
    ];

    // API call function
    const loadDeviceTimeline = async (pageToLoad = null) => {
        if (!systemId.trim()) {
            setError('System ID is required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const params = {
                systemId: systemId.trim(),
                duration,
                page: pageToLoad || selectedPage,
                size: itemsPerPage,
                sortBy: 'ts',
                sortDir: sortDirection,
                eventsOnly
            };

            const data = await fetchDeviceTimeline(params);
            
            // Check if the API returned an error
            if (data.statusCode && data.statusCode !== 200) {
                throw new Error(data.errorMessage || 'Failed to fetch device timeline data');
            }
            
            setAllEvents(data.events);
            setEvents(data.events);
            setSessions(data.sessions || []);
            setRdSessionSummary(data.rdSessionSummary);
            setPagination(data.pagination);
            setHasMoreData(data.pagination?.hasNext || false);
            
        } catch (err) {
            console.error('Error fetching device timeline:', err);
            setError('Failed to fetch device timeline data. Please check the System ID and try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        setSelectedPage(newPage);
        loadDeviceTimeline(newPage);
    };

    // Handle items per page change
    const handleItemsPerPageChange = (newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setSelectedPage(1); // Reset to first page
        loadDeviceTimeline(1);
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        if (systemId.trim()) {
            setAllEvents([]);
            setSelectedPage(1);
            loadDeviceTimeline(1);
        }
    };

    // Auto-load timeline when prefilledSystemId is provided
    useEffect(() => {
        if (prefilledSystemId && prefilledSystemId.trim()) {
            setSystemId(prefilledSystemId);
            setAllEvents([]);
            setSelectedPage(1);
            loadDeviceTimeline(1);
        }
    }, [prefilledSystemId]);

    // Auto-fetch when systemId and duration are provided
    useEffect(() => {
        if (systemId.trim() && duration && !prefilledSystemId) {
            const timeoutId = setTimeout(() => {
                setAllEvents([]);
                setSelectedPage(1);
                loadDeviceTimeline(1);
            }, 500); // 500ms debounce to avoid too many API calls while typing

            return () => clearTimeout(timeoutId);
        }
    }, [systemId, duration, sortDirection, itemsPerPage]);

    // Handle load more
    const handleLoadMore = () => {
        if (hasMoreData && !loading) {
            loadDeviceTimeline(true);
        }
    };

    // Filter events based on search and categories
    const filteredEvents = allEvents.filter(event => {
        // Category filter
        if (!categoryFilters[event.eventCategory]) {
            return false;
        }
        
        // Search filter
        if (searchTerm) {
            const searchLower = searchTerm.toLowerCase();
            return (
                event.eventName.toLowerCase().includes(searchLower) ||
                event.eventCategory.toLowerCase().includes(searchLower) ||
                (event.extraDetails && JSON.stringify(event.extraDetails).toLowerCase().includes(searchLower))
            );
        }
        
        return true;
    });

    // Group events by sessions
    const groupedEvents = () => {
        if (eventsOnly || sessions.length === 0) {
            return [{ events: filteredEvents, isSession: false }];
        }
        
        // For now, return all events in one group
        // TODO: Implement proper session grouping based on session data
        return [{ events: filteredEvents, isSession: false }];
    };

    // Toggle category filter
    const toggleCategoryFilter = (category) => {
        setCategoryFilters(prev => ({
            ...prev,
            [category]: !prev[category]
        }));
    };

    // Select all categories
    const selectAllCategories = () => {
        const allSelected = Object.values(categoryFilters).every(Boolean);
        const newState = {};
        Object.keys(categoryFilters).forEach(key => {
            newState[key] = !allSelected;
        });
        setCategoryFilters(newState);
    };

    // Utility function to convert epoch timestamp to local time
    const convertEpochToLocalTime = (epochTimestamp) => {
        if (!epochTimestamp || isNaN(epochTimestamp)) {
            return epochTimestamp;
        }
        
        // Convert epoch milliseconds to Date and format as local time
        const date = new Date(epochTimestamp);
        return date.toLocaleString();
    };

    // Utility function to process extraDetails and convert epoch timestamps
    const processExtraDetails = (extraDetails) => {
        if (!extraDetails || typeof extraDetails !== 'object') {
            return extraDetails;
        }

        const processed = { ...extraDetails };
        
        // List of known epoch timestamp fields
        const epochFields = [
            'connected_at',
            'registered_at',
            'session_started_at',
            'session_ended_at',
            'disconnected_at',
            'timestamp',
            'command_ack_at',
            'url_fetch_issued_at'
        ];

        // Convert epoch fields to local time
        epochFields.forEach(field => {
            if (processed[field] && !isNaN(processed[field])) {
                processed[field] = convertEpochToLocalTime(processed[field]);
            }
        });

        // Convert collector_id to collector name
        if (processed['collector_id']) {
            processed['collector_id'] = getCollectorName(processed['collector_id']);
        }

        return processed;
    };

    // Format timestamp (handles both ISO strings and epoch timestamps)
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return '';
        
        // If it's a number (epoch), convert it
        if (typeof timestamp === 'number') {
            return convertEpochToLocalTime(timestamp);
        }
        
        // If it's an ISO string, parse and format
        const date = new Date(timestamp);
        return date.toLocaleString();
    };

    // Get event icon
    const getEventIcon = (eventCategory, eventName) => {
        switch (eventCategory) {
            case 'CONNECTION':
                return eventName.includes('CONNECTED') ? '🔌' : '🔌❌';
            case 'REGISTRATION':
                return '📝';
            case 'SESSION':
                return eventName.includes('START') ? '▶️' : '⏹️';
            case 'UPLOAD':
                return '⬆️';
            case 'MQTT':
                return '📡';
            case 'URL_FETCH':
                return '🌐';
            case 'DISCONNECT':
                return '⚠️';
            default:
                return '📋';
        }
    };

    // Get event category color
    const getCategoryColor = (category) => {
        const colors = {
            CONNECTION: 'var(--color-connection)',
            REGISTRATION: 'var(--color-registration)',
            SESSION: 'var(--color-session)',
            UPLOAD: 'var(--color-upload)',
            MQTT: 'var(--primary-blue)',
            URL_FETCH: 'var(--color-url-fetch)',
            DISCONNECT: 'var(--color-disconnect)'
        };
        return colors[category] || 'var(--primary-blue)';
    };

    // Get user-friendly display name for field
    const getFieldDisplayName = (fieldName) => {
        const fieldNameMap = {
            'collector_id': 'Diagnostic Type'
        };
        return fieldNameMap[fieldName] || fieldName;
    };

    // Get collector name from collector ID
    const getCollectorName = (collectorId) => {
        const collectorMap = {
            '539721258': 'MiniDump Collector',
            '878222076': 'ODL Collector',
            '281651091': 'MicroDump Collector'
        };
        return collectorMap[String(collectorId)] || collectorId;
    };

    // Render collector details in a user-friendly format
    const renderCollectorDetails = (collectors) => {
        if (!Array.isArray(collectors) || collectors.length === 0) {
            return null;
        }

        return (
            <div className="collector-list">
                {collectors.map((collector, index) => (
                    <div key={index} className="collector-item">
                        <div className="collector-header">
                            <span className="collector-icon">📋</span>
                            <span className="collector-name">{collector.collectorName || 'Unknown'}</span>
                        </div>
                        <div className="collector-details">
                            <div className="collector-detail-row">
                                <span className="collector-detail-label">Command:</span>
                                <span className="collector-detail-value">{collector.commandName || 'N/A'}</span>
                            </div>
                            {collector.logMaskDescription && (
                                <div className="collector-detail-row">
                                    <span className="collector-detail-label">Description:</span>
                                    <span className="collector-detail-value">{collector.logMaskDescription}</span>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    // Toggle event details
    const toggleEventDetails = (eventIndex) => {
        const detailsElement = document.getElementById(`event-details-${eventIndex}`);
        const toggleButton = document.getElementById(`toggle-button-${eventIndex}`);
        
        if (detailsElement && toggleButton) {
            const isVisible = detailsElement.classList.contains('show');
            
            if (isVisible) {
                detailsElement.classList.remove('show');
                toggleButton.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
                    </svg>
                    Show Details
                `;
            } else {
                detailsElement.classList.add('show');
                toggleButton.innerHTML = `
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path d="M4 8a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7A.5.5 0 0 1 4 8z"/>
                    </svg>
                    Hide Details
                `;
            }
        }
    };

    return (
        <div className="device-timeline-container">
            {/* Header Controls */}
            <div className="device-timeline-header">
                <div className="device-timeline-header-content">
                    <div className="device-timeline-controls">
                        <form onSubmit={handleSubmit} className="device-timeline-form">
                            <div className="form-group">
                                <input
                                    type="text"
                                    value={systemId}
                                    onChange={(e) => setSystemId(e.target.value)}
                                    placeholder="Enter System ID"
                                    className="system-id-input"
                                    disabled={loading}
                                />
                            </div>
                            <div className="form-group">
                                <select
                                    value={duration}
                                    onChange={(e) => setDuration(e.target.value)}
                                    className="duration-select"
                                    disabled={loading}
                                >
                                    {timeRangeOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <select
                                    value={sortDirection}
                                    onChange={(e) => setSortDirection(e.target.value)}
                                    className="duration-select"
                                    disabled={loading}
                                >
                                    {sortOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <button type="submit" className="fetch-button" disabled={loading}>
                                {loading ? 'Loading...' : 'Refresh'}
                            </button>
                        </form>
                    </div>
                    
                    {/* Pagination Controls */}
                    {pagination && pagination.totalPages > 0 && (
                        <div className="pagination-controls">
                            <div className="form-group">
                                <select
                                    value={selectedPage}
                                    onChange={(e) => handlePageChange(parseInt(e.target.value))}
                                    className="page-select"
                                    disabled={loading}
                                >
                                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(page => (
                                        <option key={page} value={page}>
                                            Page {page} of {pagination.totalPages}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <select
                                    value={itemsPerPage}
                                    onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value))}
                                    className="items-per-page-select"
                                    disabled={loading}
                                >
                                    {itemsPerPageOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="device-timeline-content">
                {/* Sidebar */}
                <aside className="device-timeline-sidebar">
                    {/* Session Summary */}
                    {rdSessionSummary && (
                        <div className="sidebar-section">
                            <div className="session-summary">
                                <div className="sidebar-title">Session Summary</div>
                                <div className="summary-item">
                                    <span className="summary-label">Number of RD Sessions:</span>
                                    <span className="summary-value">{rdSessionSummary.totalRdSessions}</span>
                                </div>
                                {rdSessionSummary.uploadsPerCollector && Object.keys(rdSessionSummary.uploadsPerCollector).length > 0 && (
                                    <>
                                        <div className="summary-item" style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
                                            <span className="summary-label" style={{ fontWeight: '600' }}>Number of Uploads per Collector:</span>
                                        </div>
                                        {Object.entries(rdSessionSummary.uploadsPerCollector).map(([collector, count]) => (
                                            <div key={collector} className="summary-item" style={{ paddingLeft: '1rem' }}>
                                                <span className="summary-label">• {collector}:</span>
                                                <span className="summary-value">{count}</span>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Search */}
                    <div className="sidebar-section">
                        <div className="search-container">
                            <input
                                type="text"
                                className="search-input"
                                placeholder="🔍 Search events..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Event Categories */}
                    <div className="sidebar-section">
                        <div className="sidebar-title">
                            <span>Event Categories</span>
                            <button onClick={selectAllCategories} className="select-all-btn">
                                {Object.values(categoryFilters).every(Boolean) ? 'Deselect All' : 'Select All'}
                            </button>
                        </div>
                        <div className="checkbox-group">
                            {Object.entries(categoryFilters).map(([category, checked]) => (
                                <div key={category} className="checkbox-item">
                                    <input
                                        type="checkbox"
                                        id={`category${category}`}
                                        checked={checked}
                                        onChange={() => toggleCategoryFilter(category)}
                                    />
                                    <label htmlFor={`category${category}`}>
                                        <span 
                                            className="color-indicator" 
                                            style={{ backgroundColor: getCategoryColor(category) }}
                                        ></span>
                                        {category.charAt(0) + category.slice(1).toLowerCase()}
                                    </label>
                                </div>
                            ))}
                        </div>
                    </div>
                </aside>

                {/* Main Timeline */}
                <main className="device-timeline-main">
                    {error && (
                        <div className="error-message">
                            <div className="error-icon">⚠️</div>
                            <div>
                                <h3>Error Loading Timeline</h3>
                                <p>{error}</p>
                                <button onClick={() => loadDeviceTimeline(false)} className="retry-button">
                                    Retry
                                </button>
                            </div>
                        </div>
                    )}

                    {!error && filteredEvents.length === 0 && !loading && systemId && (
                        <div className="no-data-message">
                            <div className="no-data-icon">📋</div>
                            <h3>No Events Found</h3>
                            <p>No events found for the specified criteria. Try adjusting your filters or time range.</p>
                        </div>
                    )}

                    {!error && !systemId && !loading && (
                        <div className="welcome-message">
                            <div className="welcome-icon">🔍</div>
                            <h3>Device Timeline</h3>
                            <p>Enter a System ID and select a time range to view device events and sessions.</p>
                        </div>
                    )}

                    {filteredEvents.length > 0 && (
                        <div className="timeline">
                            {groupedEvents().map((group, groupIndex) => (
                                <div key={groupIndex} className="timeline-group">
                                    {group.events.map((event, eventIndex) => {
                                        const globalIndex = groupIndex * 1000 + eventIndex; // Unique index
                                        return (
                                            <div key={globalIndex} className="timeline-event">
                                                <div className={`event-card category-${event.eventCategory}`}>
                                                    <div className="event-header">
                                                        <span className="event-timestamp">
                                                            {formatTimestamp(event.timestamp)}
                                                        </span>
                                                        <span 
                                                            className={`event-category category-${event.eventCategory}`}
                                                            style={{ backgroundColor: `${getCategoryColor(event.eventCategory)}20`, color: getCategoryColor(event.eventCategory) }}
                                                        >
                                                            {event.eventCategory}
                                                        </span>
                                                    </div>
                                                    <div className="event-name-row">
                                                        <span className="event-icon">
                                                            {getEventIcon(event.eventCategory, event.eventName)}
                                                        </span>
                                                        <h3 className="event-name">{event.eventName}</h3>
                                                    </div>
                                                    <p className="event-summary">
                                                        {event.eventCategory} event occurred
                                                    </p>
                                                    {event.extraDetails && Object.keys(event.extraDetails).length > 0 && (
                                                        <>
                                                            <button 
                                                                id={`toggle-button-${globalIndex}`}
                                                                className="toggle-details" 
                                                                onClick={() => toggleEventDetails(globalIndex)}
                                                            >
                                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                                                                    <path d="M8 4a.5.5 0 0 1 .5.5v3h3a.5.5 0 0 1 0 1h-3v3a.5.5 0 0 1-1 0v-3h-3a.5.5 0 0 1 0-1h3v-3A.5.5 0 0 1 8 4z"/>
                                                                </svg>
                                                                Show Details
                                                            </button>
                                                            <div id={`event-details-${globalIndex}`} className="event-details">
                                                                {Object.entries(processExtraDetails(event.extraDetails)).map(([key, value]) => {
                                                                    // Special handling for collectors array
                                                                    if (key === 'collectors' && Array.isArray(value)) {
                                                                        return (
                                                                            <div key={key} className="detail-row detail-row-full">
                                                                                <span className="detail-label">{getFieldDisplayName(key)}:</span>
                                                                                <div className="detail-value">
                                                                                    {renderCollectorDetails(value)}
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    }
                                                                    // Default rendering for other fields
                                                                    return (
                                                                        <div key={key} className="detail-row">
                                                                            <span className="detail-label">{getFieldDisplayName(key)}:</span>
                                                                            <span className="detail-value">
                                                                                {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}

                            {/* Pagination Info */}
                            {pagination && (
                                <div className="pagination-info">
                                    <p>
                                        Page {pagination.currentPage} of {pagination.totalPages} 
                                        ({pagination.totalElements} total events)
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}

export default DeviceTimeline;
